import React, { useCallback } from 'react';
import { useHistory, useRouteMatch, useLocation, matchPath } from 'react-router-dom';
import type { LocationDescriptor } from 'history';
import { Route, RouteProps } from '@modernhealth/web-analytics';

/**
 * Modal Routes
 *
 * Why?
 * - Modal routes enable deep linking to modals which supports backend driven
 *   content.
 * - Modal routes enable shared modal usage without having to implement a modal
 *   and modal visibility state in every page where we want to use it.
 *
 * In order to better support the creation of modal routes, we have created a
 * creator toolkit which generates the modal routes and helper navigation
 * methods for your domain.
 *
 * The modal toolkit supports both single and multi-path modal routes. This
 * allows you to specify contextual information for the modal that doesn't
 * exist in the base path. As an example, `provider/:providerUuid` as the modal
 * path would allow the modal to render a specific provider based on the uuid
 * and access that via standard route params. See the tests for example usages.
 *
 * How to use the Modal Toolkit:
 * 1. Import the createModalPath function, the createModalRouteToolkit
 * function, and the useNavigateBackFromModalRoute hook into the modal page
 * component.
 * 2. Create the modal path object using the createModalPath function.
 * 3. Use the useNavigateBackFromModalRoute hook within the modal page
 * component as the on close handler.
 * 4. Generate and export a route toolkit by using the modal path and modal
 * page component with the createModalRouteToolkit function.
 * 5. Add the ModalRoute from the toolkit to the domain router and configure
 * the basePath and analytics data.
 * 6. Use the useNavigate hook from the toolkit in other pages where you want
 * to link to the modal.
 */

const pathSymbol = Symbol('ModalPath');

export interface ModalPath<_ extends ModalPathParams = ModalPathParams> {
    __type: typeof pathSymbol;
    path: string;
}

type ModalPathParams = Record<string, string | number> | undefined;

type ParamsFromModalPath<D> = D extends ModalPath<infer P> ? P : undefined;

export function createModalPath<P extends ModalPathParams = undefined>(path: string): ModalPath<P> {
    return { __type: pathSymbol, path };
}

interface ModalRouteProps extends Omit<RouteProps, 'path'> {
    basePath: string;
    modalPath: string;
}

/**
 * react-router Route accepts an array of paths which would allow
 * us to handle this with one route declaration; however, our
 * analytics Route forces us to only define a single path to make
 * data capturing simpler. Easy work around for us here is to
 * define this as two distinct routes.
 */
export function ModalRoute({ basePath, modalPath, ...routeProps }: ModalRouteProps) {
    return (
        <>
            <Route path={`${basePath}/${modalPath}`} exact {...routeProps} />
            <Route path={`${basePath}/*/${modalPath}`} exact {...routeProps} />
        </>
    );
}

export function replacePathParams<P extends ModalPath>(
    path: string,
    params: ParamsFromModalPath<P>,
) {
    if (!params) {
        return path;
    }

    return Object.entries(params).reduce(
        (accum, [key, value]) => accum.replace(`:${key}`, `${value}`),
        path,
    );
}

type GetLocationDescriptor<P extends ModalPath> = undefined extends ParamsFromModalPath<P>
    ? () => LocationDescriptor
    : (params: ParamsFromModalPath<P>) => LocationDescriptor;

type Navigate<P extends ModalPath> = undefined extends ParamsFromModalPath<P>
    ? () => void
    : (params: ParamsFromModalPath<P>) => void;

export function useNavigateToModalRoute<P extends ModalPath>(
    modalPath: P,
): {
    getLocationDescriptor: GetLocationDescriptor<P>;
    navigate: Navigate<P>;
} {
    const history = useHistory();
    const location = useLocation();
    const { url } = useRouteMatch();

    const getLocationDescriptor = useCallback(
        (params?: ParamsFromModalPath<P>) => ({
            pathname: `${url}/${replacePathParams(modalPath.path, params)}`,
            search: location.search,
        }),
        [modalPath, url, location.search],
    );

    const navigate = useCallback(
        (params?: ParamsFromModalPath<P>) => {
            const locationDescriptor = getLocationDescriptor(params);
            history.push(locationDescriptor);
        },
        [history, getLocationDescriptor],
    );

    return {
        getLocationDescriptor,
        navigate,
    };
}

interface ModalRouteToolkitProps
    extends Omit<RouteProps, 'path' | 'component' | 'render' | 'children'> {
    basePath: string;
}

export function createModalRouteToolkit<P extends ModalPath>({
    modalPath,
    component,
}: {
    modalPath: P;
    component: RouteProps['component'];
}) {
    return {
        ModalRoute({ basePath, ...routeProps }: ModalRouteToolkitProps) {
            return (
                <ModalRoute
                    basePath={basePath}
                    modalPath={modalPath.path}
                    component={component}
                    {...routeProps}
                />
            );
        },
        useNavigate() {
            return useNavigateToModalRoute<P>(modalPath);
        },
    };
}

export function useNavigateBackFromModalRoute(modalPath: ModalPath) {
    const { path, url } = useRouteMatch();
    const history = useHistory();
    const location = useLocation();

    const closeModal = useCallback(() => {
        const match = matchPath(url, { path: path.replace(modalPath.path, '') });

        // If there is sufficient history, go back, otherwise navigate forward
        // to close the modal and go to the underlying route. There is not
        // sufficient history to use the back functionality if the member
        // navigated to the modal through a deep link in a new browser tab or
        // window.
        if (!match || history.length > 2) {
            history.goBack();
        } else {
            history.push({ pathname: match.url, search: location.search });
        }
    }, [history, location.search, url, path, modalPath.path]);

    return {
        closeModal,
    };
}
